import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

/* ─────────────────────────────────────────────────────────────
   HERO IMAGE — paste your Cloudinary URL here.
   Leave empty to show the labelled placeholder.
   ───────────────────────────────────────────────────────────── */
const HERO_IMAGE = "https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/f_auto/q_auto/OverSized_Shirt_mzeojt.png";
const HERO_ALT = "Black Oversized Shirt, worn by a model in a studio";

/* ANATOMY IMAGE — replaces the drawn tee diagram. Leave empty to use the drawn diagram. */
// Paste the SHIRT anatomy image URL here. (The old value was the Terry Tee image.)
const ANATOMY_IMAGE = "";
const ANATOMY_ALT =
  "Oversized Shirt anatomy: oversized fit, half sleeves, straight hem and shoulder seam";
// Numbered squares laid over the image. Keep false if the image has its own callouts.
const SHOW_MARKERS = false;
const COLLECTION_PATH = "/collections/OverSized Shirt";

const MARQUEE = [
  "240 GSM",
  "100% COTTON",
  "OVERSIZED FIT",
  "HALF SLEEVES",
  "UNISEX STYLE",
  "STRAIGHT HEM",
  "FRONT SHOULDER",
];

// Weight scale — gsm values from THE BLANK range
const SCALE_MIN = 140;
const SCALE_MAX = 280;
const SCALE_TOP = 260; // longest bar on mobile
const SCALE = [
  { gsm: 160, label: "SUPIMA TEE" },
  { gsm: 180, label: "RINGER · RAGLAN" },
  { gsm: 220, label: "POLO TEE" },
  { gsm: 240, label: "OVERSIZED SHIRT — THIS SHIRT", active: true },
  { gsm: 260, label: "TERRY OVERSIZED TEE" },
];
const scalePos = (gsm) => ((gsm - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;

// Anatomy — marker positions are % of the diagram box
const ANATOMY = [
  {
    n: "01",
    title: "Oversized fit",
    text: "Cut wide through the body and arms, so it falls away from you instead of clinging. Room to move, with shape that stays.",
    left: 50,
    top: 59,
  },
  {
    n: "02",
    title: "Half sleeves",
    text: "Sleeves that stop in the right place. Cool and free on warm days, clean when you layer.",
    left: 16,
    top: 38,
  },
  {
    n: "03",
    title: "Straight hem",
    text: "A flat, straight hem that sits clean worn out and untucked. It finishes the look without any effort.",
    left: 50,
    top: 82,
  },
  {
    n: "04",
    title: "Drop shoulder",
    text: "The seam falls past your shoulder, so the sleeves hang wide and easy. Instant relaxed shape, on any build.",
    left: 29,
    top: 30,
  },
];

const OtEyebrow = ({ children, invert = false }) => (
  <div className={`os-eyebrow${invert ? " os-eyebrow--invert" : ""}`}>
    <span className="os-eyebrow__line" aria-hidden="true" />
    <span className="os-eyebrow__text">{children}</span>
  </div>
);

const FeaturedOverSizedShirt = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/products`,
          { params: { collection: "OverSized Shirt" } }
        );
        setProducts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const displayProducts = products.slice(0, 8);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600;700;800&display=swap');

        /* ══════════════════════════════════════════════
           OVERSIZED SHIRT — HERO + SPEC STORY  (prefix: os-)
           ══════════════════════════════════════════════ */
        .os-root {
          --os-ink: #0a0a0a;
          --os-white: #ffffff;
          --os-mist: #f8f8f8;
          --os-line: #d8d8d8;
          --os-muted: #4a4a4a;
          --os-pad: 48px;
          font-family: 'Exo 2', sans-serif;
          color: var(--os-ink);
          background: var(--os-white);
          width: 100%;
          overflow-x: hidden;
        }
        .os-root *, .os-root *::before, .os-root *::after {
          box-sizing: border-box;
          border-radius: 0;
        }

        /* shared */
        .os-eyebrow { display: flex; align-items: center; gap: 14px; }
        .os-eyebrow__line { display: block; flex: 0 0 32px; width: 32px; height: 1px; background: var(--os-ink); }
        .os-eyebrow__text {
          font-size: 11px; font-weight: 600; letter-spacing: 0.34em; text-transform: uppercase;
        }
        .os-eyebrow--invert .os-eyebrow__line { background: var(--os-white); }

        .os-btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-height: 52px; padding: 0 36px;
          border: 1px solid var(--os-ink);
          text-decoration: none; font-family: inherit;
          font-size: 12px; font-weight: 600; letter-spacing: 0.3em; text-transform: uppercase;
          cursor: pointer;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .os-btn--dark { background: var(--os-ink); color: var(--os-white); }
        .os-btn--dark:hover { background: var(--os-white); color: var(--os-ink); }
        .os-btn--light {
          width: 100%; min-height: 56px;
          background: var(--os-white); color: var(--os-ink); border-color: var(--os-white);
          font-weight: 700;
        }
        .os-btn--light:hover { background: var(--os-ink); color: var(--os-white); }
        .os-btn:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }

        /* hero */
        .os-hero { background: var(--os-mist); overflow: hidden; }
        .os-hero__inner { max-width: 1280px; margin: 0 auto; padding: 40px var(--os-pad) 0; }
        .os-hero__meta {
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px;
        }
        .os-hero__meta-right {
          font-size: 11px; font-weight: 500; letter-spacing: 0.34em; color: var(--os-muted);
        }
        .os-hero__grid { display: flex; flex-wrap: wrap; align-items: stretch; margin-top: 24px; }
        .os-hero__copy {
          position: relative; z-index: 2;
          flex: 0 1 620px; min-width: 0;
          display: flex; flex-direction: column; justify-content: space-between;
          gap: 48px; padding: 24px 0 56px;
        }
        .os-hero__title {
          margin: 0; font-weight: 800;
          font-size: clamp(84px, 13vw, 188px);
          line-height: 0.84; letter-spacing: -0.05em; text-transform: uppercase;
        }
        .os-hero__title span { display: block; }
        .os-hero__title-sub {
          font-size: 0.2em; font-weight: 500; letter-spacing: 0.5em; line-height: 1; margin-top: 1em;
        }
        .os-hero__cta-block { display: flex; flex-direction: column; gap: 28px; max-width: 440px; }
        .os-hero__line { margin: 0; font-size: 26px; line-height: 1.3; font-weight: 300; }
        .os-hero__media {
          position: relative; z-index: 1;
          flex: 1 1 440px; min-width: 0; min-height: 680px;
          background: #e6e6e6; border: 1px solid #cfcfcf; overflow: hidden;
        }
        .os-hero__img {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; object-position: center top; display: block;
        }
        .os-hero__placeholder {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 10px; padding: 32px; text-align: center;
        }
        .os-hero__ph-tag { font-size: 12px; font-weight: 600; letter-spacing: 0.28em; color: #2a2a2a; }
        .os-hero__ph-note { max-width: 300px; font-size: 14px; line-height: 1.5; color: #3a3a3a; }
        .os-hero__fig {
          position: absolute; bottom: 18px;
          font-size: 11px; font-weight: 500; letter-spacing: 0.24em;
          color: var(--os-ink); background: var(--os-mist); padding: 4px 8px;
        }
        .os-hero__fig--l { left: 20px; }
        .os-hero__fig--r { right: 20px; }

        /* strip */
        .os-strip { background: var(--os-ink); color: var(--os-white); }
        .os-strip__inner {
          max-width: 1280px; margin: 0 auto; padding: 22px var(--os-pad);
          list-style: none;
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
          gap: 16px 20px;
        }
        .os-strip__item {
          display: flex; align-items: center; gap: 20px;
          font-size: 11px; font-weight: 600; letter-spacing: 0.2em;
        }
        .os-strip__dot { display: block; width: 6px; height: 6px; background: var(--os-white); }

        /* statement */
        .os-statement__inner {
          max-width: 1280px; margin: 0 auto; padding: 128px var(--os-pad) 96px;
          display: flex; flex-wrap: wrap; gap: 40px 96px;
        }
        .os-statement__label { flex: 0 0 220px; padding-top: 14px; }
        .os-statement__body { flex: 1 1 520px; min-width: 0; }
        .os-statement__big {
          margin: 0; font-size: clamp(30px, 3.6vw, 50px);
          line-height: 1.18; font-weight: 300; letter-spacing: -0.02em;
        }
        .os-statement__small {
          margin: 32px 0 0; max-width: 560px;
          font-size: 17px; line-height: 1.65; color: var(--os-muted);
        }

        /* bento */
        .os-bento__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 var(--os-pad) 16px;
          display: flex; flex-wrap: wrap; gap: 16px;
        }
        .os-tile {
          min-width: 0; min-height: 480px; padding: 40px;
          display: flex; flex-direction: column; justify-content: space-between; gap: 32px;
        }
        .os-tile--ink { background: var(--os-ink); color: var(--os-white); }
        .os-tile--mist { background: var(--os-mist); }
        .os-tile--weight { flex: 1.35 1 520px; }
        .os-tile--mix { flex: 1 1 400px; }
        .os-tile__copy { margin: 0; max-width: 440px; font-size: 18px; line-height: 1.55; font-weight: 300; }
        .os-weight { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 20px; }
        .os-weight__num {
          font-size: clamp(140px, 19vw, 260px); font-weight: 800;
          line-height: 0.78; letter-spacing: -0.06em;
        }
        .os-weight__unit { padding-bottom: 8px; font-size: 28px; font-weight: 300; letter-spacing: 0.2em; }
        .os-mix { display: flex; flex-direction: column; gap: 20px; }
        .os-mix__num {
          font-size: clamp(72px, 9vw, 128px); font-weight: 800;
          line-height: 0.85; letter-spacing: -0.05em;
        }
        .os-mix__bar { display: flex; width: 100%; height: 28px; }
        .os-mix__cotton { flex: 90 1 0; background: var(--os-ink); }
        .os-mix__poly { flex: 10 1 0; background: #bdbdbd; }
        .os-mix__labels {
          display: flex; justify-content: space-between; flex-wrap: wrap; gap: 16px;
          font-size: 12px; font-weight: 600; letter-spacing: 0.22em;
        }
        .os-mix__labels-muted { color: var(--os-muted); }

        /* weight scale — desktop ruler */
        .os-scale__inner { max-width: 1280px; margin: 0 auto; padding: 0 var(--os-pad) 16px; }
        .os-scale__panel { background: var(--os-mist); padding: 40px; }
        .os-scale__scroll { margin-top: 56px; overflow-x: auto; }
        .os-scale__track { position: relative; min-width: 880px; height: 150px; }
        .os-scale__axis { position: absolute; left: 0; right: 0; top: 20px; height: 1px; background: var(--os-ink); }
        .os-scale__tick {
          position: absolute; top: 0; transform: translateX(-50%);
          display: flex; flex-direction: column; align-items: center; gap: 12px;
        }
        .os-scale__stem { display: block; width: 1px; height: 41px; background: var(--os-ink); }
        .os-scale__gsm { font-size: 28px; font-weight: 300; line-height: 1; letter-spacing: -0.02em; }
        .os-scale__name {
          font-size: 11px; font-weight: 500; letter-spacing: 0.22em;
          text-align: center; color: var(--os-muted); white-space: nowrap;
        }
        .os-scale__tick--active .os-scale__marker {
          display: block; width: 14px; height: 14px; margin-top: 13px; background: var(--os-ink);
        }
        .os-scale__tick--active .os-scale__stem { height: 20px; margin-top: -6px; }
        .os-scale__tick--active .os-scale__gsm { font-size: 44px; font-weight: 800; letter-spacing: -0.03em; }
        .os-scale__tick--active .os-scale__name { font-weight: 700; color: var(--os-ink); }

        /* weight scale — mobile bars (hidden on desktop) */
        .os-bars { display: none; margin: 0; padding: 0; list-style: none; }
        .os-bar__top {
          display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
          font-size: 10px; font-weight: 500; letter-spacing: 0.16em; color: var(--os-muted);
        }
        .os-bar__gsm { font-size: 14px; font-weight: 400; letter-spacing: 0; color: var(--os-ink); }
        .os-bar__track { height: 8px; margin-top: 6px; background: #e6e6e6; }
        .os-bar__fill { height: 100%; background: #bdbdbd; }
        .os-bar--active .os-bar__top { font-weight: 700; color: var(--os-ink); }
        .os-bar--active .os-bar__gsm { font-weight: 800; }
        .os-bar--active .os-bar__fill { background: var(--os-ink); }

        /* anatomy */
        .os-anatomy__inner { max-width: 1280px; margin: 0 auto; padding: 112px var(--os-pad) 120px; }
        .os-anatomy__title {
          margin: 24px 0 72px; max-width: 900px;
          font-size: clamp(44px, 6vw, 88px); font-weight: 800;
          line-height: 0.95; letter-spacing: -0.04em; text-transform: uppercase;
        }
        .os-anatomy__grid { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 48px 80px; }
        .os-diagram {
          position: relative; flex: 1 1 440px; max-width: 560px; min-width: 0;
          aspect-ratio: 1 / 1; padding: 6%; background: var(--os-mist);
        }
        .os-diagram__svg { display: block; width: 100%; height: 100%; }
        .os-diagram__marker {
          position: absolute; transform: translate(-50%, -50%);
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          background: var(--os-ink); color: var(--os-white);
          font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
        }
        .os-legend {
          flex: 1 1 400px; min-width: 0; margin: 0; padding: 0; list-style: none;
          display: flex; flex-direction: column;
        }
        .os-legend__row { display: flex; gap: 24px; padding: 28px 0; border-top: 1px solid var(--os-line); }
        .os-legend__row:first-child { border-top-color: var(--os-ink); }
        .os-legend__row:last-child { border-bottom: 1px solid var(--os-ink); }
        .os-legend__n { flex: 0 0 36px; padding-top: 4px; font-size: 13px; font-weight: 700; letter-spacing: 0.12em; }
        .os-legend__body { display: flex; flex-direction: column; gap: 8px; }
        .os-legend__title { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; }
        .os-legend__text { margin: 0; max-width: 420px; font-size: 16px; line-height: 1.6; color: var(--os-muted); }

        /* closing */
        .os-close { background: var(--os-ink); color: var(--os-white); }
        .os-close__inner {
          max-width: 1280px; margin: 0 auto; padding: 120px var(--os-pad);
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 48px;
        }
        .os-close__copy { flex: 1 1 560px; min-width: 0; display: flex; flex-direction: column; gap: 32px; }
        .os-close__title {
          margin: 0; font-size: clamp(40px, 5.4vw, 76px); font-weight: 800;
          line-height: 0.98; letter-spacing: -0.04em; text-transform: uppercase;
        }
        .os-close__buy { flex: 0 1 340px; display: flex; flex-direction: column; gap: 20px; }
        .os-close__row {
          display: flex; justify-content: space-between; align-items: baseline; gap: 16px;
          padding-top: 16px; border-top: 1px solid var(--os-white);
          font-size: 12px; font-weight: 600; letter-spacing: 0.24em;
        }

        .os-diagram--img { aspect-ratio: auto; padding: 0; }
        .os-diagram__img { display: block; width: 100%; height: auto; }
        .os-hero__sub { margin: 0; font-size: 16px; line-height: 1.6; color: var(--os-muted); }
        .os-scale__note { margin: 16px 0 0; font-size: 16px; line-height: 1.5; color: var(--os-muted); }
        .os-close__tag {
          margin: 0; font-size: 12px; font-weight: 500; letter-spacing: 0.28em;
          text-transform: uppercase; color: #bdbdbd;
        }

        /* unisex + in-house */
        .os-make__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 var(--os-pad) 16px;
          display: flex; flex-wrap: wrap; gap: 16px;
        }
        .os-make__cell {
          flex: 1 1 420px; min-width: 0; min-height: 360px; padding: 40px;
          display: flex; flex-direction: column; justify-content: space-between; gap: 32px;
          background: var(--os-mist);
        }
        .os-make__cell--ink { background: var(--os-ink); color: var(--os-white); }
        .os-make__title {
          margin: 0; font-size: clamp(32px, 4vw, 56px); font-weight: 800;
          line-height: 0.98; letter-spacing: -0.03em; text-transform: uppercase;
        }
        .os-make__text { margin: 0; max-width: 440px; font-size: 18px; line-height: 1.55; font-weight: 300; }

        @media (prefers-reduced-motion: reduce) {
          .os-btn { transition: none; }
        }

        /* ══════════════════════════════════════════════
           TABLET + MOBILE  (≤ 900px) — compact layout
           ══════════════════════════════════════════════ */
        @media (max-width: 900px) {
          .os-root { --os-pad: 20px; }

          /* hero: title → image (full-bleed) → line + CTA */
          .os-hero__inner { padding-top: 20px; }
          .os-hero__meta-right { display: none; }
          .os-hero__grid {
            flex-direction: column; flex-wrap: nowrap; gap: 16px; margin-top: 12px;
          }
          .os-hero__copy { display: contents; }
          .os-hero__title {
            order: 1; font-size: clamp(64px, 19vw, 120px);
          }
          .os-hero__media {
            order: 2; flex: none; min-height: 0; aspect-ratio: 5 / 6;
            margin: 0 calc(-1 * var(--os-pad)); border: 0;
          }
          .os-hero__cta-block {
            order: 3; max-width: none; gap: 16px; padding-bottom: 28px;
          }
          .os-hero__line { font-size: 18px; line-height: 1.35; }
          .os-hero__cta-block .os-btn { width: 100%; min-height: 50px; }
          .os-hero__fig { bottom: 12px; font-size: 10px; }
          .os-hero__fig--l { left: 16px; }
          .os-hero__fig--r { right: 16px; }

          /* strip: one scrolling line */
          .os-strip__inner {
            flex-wrap: nowrap; justify-content: flex-start; gap: 20px;
            padding: 14px var(--os-pad); overflow-x: auto; scrollbar-width: none;
          }
          .os-strip__inner::-webkit-scrollbar { display: none; }
          .os-strip__item { gap: 20px; font-size: 11px; letter-spacing: 0.22em; white-space: nowrap; }

          /* statement: short */
          .os-statement__inner { padding: 44px var(--os-pad) 32px; gap: 16px; }
          .os-statement__label { flex: 1 1 100%; padding-top: 0; }
          .os-statement__big { font-size: clamp(22px, 6vw, 30px); line-height: 1.25; }
          .os-statement__small { display: none; }

          /* bento: two compact tiles side by side */
          .os-bento__inner {
            display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding-bottom: 8px;
          }
          .os-tile {
            min-height: 0; padding: 16px; gap: 18px; justify-content: flex-start;
          }
          .os-tile .os-eyebrow { gap: 8px; align-items: flex-start; }
          .os-tile .os-eyebrow__line { flex-basis: 14px; width: 14px; margin-top: 6px; }
          .os-tile .os-eyebrow__text { font-size: 9px; letter-spacing: 0.16em; line-height: 1.3; }
          .os-weight { gap: 6px; }
          .os-weight__num { font-size: clamp(64px, 20vw, 110px); }
          .os-weight__unit { font-size: 12px; letter-spacing: 0.16em; padding-bottom: 2px; }
          .os-mix { gap: 12px; }
          .os-mix__num { font-size: clamp(34px, 11vw, 60px); }
          .os-mix__bar { height: 16px; }
          .os-mix__labels { flex-direction: column; gap: 4px; font-size: 9px; letter-spacing: 0.14em; }
          .os-tile__copy { font-size: 12.5px; line-height: 1.45; }

          /* weight scale: bars instead of side-scrolling ruler */
          .os-scale__inner { padding-bottom: 8px; }
          .os-scale__panel { padding: 20px 16px; }
          .os-scale__scroll { display: none; }
          .os-bars { display: flex; flex-direction: column; gap: 12px; margin-top: 20px; }

          /* anatomy: smaller diagram + 2x2 legend */
          .os-anatomy__inner { padding: 48px var(--os-pad) 56px; }
          .os-anatomy__title {
            margin: 16px 0 24px; font-size: clamp(30px, 9vw, 44px);
          }
          .os-anatomy__grid { gap: 20px; }
          .os-diagram { flex: 1 1 100%; max-width: 260px; margin: 0 auto; }
          .os-diagram__marker { width: 26px; height: 26px; font-size: 11px; }
          .os-legend {
            flex: 1 1 100%; display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px;
          }
          .os-legend__row {
            flex-direction: column; gap: 6px; padding: 14px 0;
          }
          .os-legend__row:nth-child(2) { border-top-color: var(--os-ink); }
          .os-legend__row:last-child { border-bottom: 0; }
          .os-legend__n { flex: none; padding-top: 0; font-size: 11px; }
          .os-legend__body { gap: 4px; }
          .os-legend__title { font-size: 15px; }
          .os-legend__text { font-size: 12.5px; line-height: 1.45; }

          .os-diagram--img { max-width: none; margin: 0; }

          /* hero sub + scale note */
          .os-hero__sub { font-size: 14px; line-height: 1.5; }
          .os-hero__cta-block { gap: 12px; }
          .os-scale__note { font-size: 13px; margin-top: 10px; }

          /* unisex + in-house */
          .os-make__inner { padding-bottom: 8px; gap: 8px; }
          .os-make__cell { flex: 1 1 100%; min-height: 0; padding: 22px 20px; gap: 14px; }
          .os-make__title { font-size: clamp(26px, 8vw, 36px); }
          .os-make__text { font-size: 14px; line-height: 1.5; }
          .os-close__tag { font-size: 10px; letter-spacing: 0.2em; }

          /* closing */
          .os-close__inner { padding: 52px var(--os-pad); gap: 28px; }
          .os-close__copy { flex: 1 1 100%; gap: 18px; }
          .os-close__title { font-size: clamp(32px, 10vw, 52px); }
          .os-close__buy { flex: 1 1 100%; gap: 14px; }
        }

        /* ══════════════════════════════════════════════
           FEATURED PRODUCTS GRID  (prefix: fos-) — from original
           ══════════════════════════════════════════════ */
        .fos-root {
          background: #f8f8f8;
          padding: 80px 0 72px;
          font-family: 'Exo 2', sans-serif;
        }

        /* ── HEADER ── */
        .fos-header {
          max-width: 1400px;
          margin: 0 auto 40px;
          padding: 0 48px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
        }

        .fos-eyebrow {
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.5em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.4);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .fos-eyebrow::before {
          content: '';
          width: 24px;
          height: 1px;
          background: rgba(0,0,0,0.2);
          display: block;
          flex-shrink: 0;
        }

        .fos-title {
          font-size: clamp(28px, 4vw, 56px);
          font-weight: 200;
          color: #000;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1;
          margin: 0;
        }
        .fos-title span { font-weight: 600; }

        .fos-cta-header {
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: #000;
          text-decoration: none;
          position: relative;
          padding-bottom: 6px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .fos-cta-header::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 100%;
          height: 1px;
          background: rgba(0,0,0,0.25);
          transition: background 0.25s ease;
        }
        .fos-cta-header:hover::after { background: #000; }

        /* ── RULE ── */
        .fos-rule {
          max-width: 1400px;
          margin: 0 auto 36px;
          padding: 0 48px;
        }
        .fos-rule hr {
          border: none;
          border-top: 1px solid rgba(0,0,0,0.08);
        }

        /* ── GRID ── */
        .fos-grid {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 48px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2px;
        }

        /* ── SKELETON ── */
        .fos-skeleton {
          background: #e8e8e8;
          aspect-ratio: 3 / 4;
          position: relative;
          overflow: hidden;
        }
        .fos-skeleton::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%);
          animation: fos-shimmer 1.6s infinite;
        }
        @keyframes fos-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* ── CARD ── */
        .fos-card {
          position: relative;
          background: #f8f8f8;
          overflow: hidden;
        }

        .fos-card-link {
          display: block;
          text-decoration: none;
        }

        .fos-img-wrap {
          width: 100%;
          aspect-ratio: 3 / 4;
          overflow: hidden;
        }

        .fos-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .fos-card:hover .fos-img { transform: scale(1.04); }

        /* Caption — no background */
        .fos-caption {
          padding: 10px 2px 14px;
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
        }

        .fos-name {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #000;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fos-price {
          font-size: 12px;
          font-weight: 300;
          letter-spacing: 0.1em;
          color: rgba(0,0,0,0.5);
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .fos-grid { grid-template-columns: repeat(2, 1fr); padding: 0 24px; }
          .fos-header { padding: 0 24px; }
          .fos-rule { padding: 0 24px; }
        }

        @media (max-width: 768px) {
          .fos-root { padding: 52px 0 48px; }
          .fos-header { flex-direction: row; align-items: flex-end; padding: 0 16px; margin-bottom: 24px; }
          .fos-title { font-size: clamp(22px, 7vw, 32px); }
          .fos-eyebrow { font-size: 8px; margin-bottom: 8px; }
          .fos-rule { padding: 0 16px; margin-bottom: 20px; }
          .fos-grid { grid-template-columns: repeat(2, 1fr); padding: 0 16px; gap: 8px; }
          .fos-caption { padding: 8px 0 10px; flex-direction: column; gap: 2px; }
          .fos-name { font-size: 9px; letter-spacing: 0.15em; }
          .fos-price { font-size: 11px; }
          .fos-cta-header { font-size: 8px; letter-spacing: 0.3em; }
        }

        @media (max-width: 360px) {
          .fos-grid { gap: 6px; padding: 0 12px; }
          .fos-header { padding: 0 12px; }
        }
      `}</style>

      <div className="os-root">
        {/* HERO */}
        <section className="os-hero" aria-labelledby="os-hero-title">
          <div className="os-hero__inner">
            <div className="os-hero__meta">
              <OtEyebrow>FEATURED COLLECTION — Nº 02</OtEyebrow>
              <span className="os-hero__meta-right">OVERSIZED SHIRT</span>
            </div>

            <div className="os-hero__grid">
              <div className="os-hero__copy">
                <h1 id="os-hero-title" className="os-hero__title">
                  <span>Over</span>
                  <span>Sized.</span>
                  <span className="os-hero__title-sub">Shirt</span>
                </h1>
                <div className="os-hero__cta-block">
                  <p className="os-hero__line">Ease, with edge.</p>
                  <p className="os-hero__sub">
                    240 GSM pure cotton, cut wide with a clean straight hem. Sharp enough to be seen in, easy enough to forget you're wearing.
                  </p>
                  <div>
                    <a className="os-btn os-btn--dark" href="#fos-collection">
                      Shop the shirt
                    </a>
                  </div>
                </div>
              </div>

              <div className="os-hero__media">
                {HERO_IMAGE ? (
                  <img className="os-hero__img" src={HERO_IMAGE} alt={HERO_ALT} />
                ) : (
                  <div className="os-hero__placeholder">
                    <span className="os-hero__ph-tag">[ HIGHLIGHT IMAGE ]</span>
                    <span className="os-hero__ph-note">
                      Black Oversized Shirt, single model, seamless studio on #f8f8f8
                    </span>
                  </div>
                )}
                <span className="os-hero__fig os-hero__fig--l">FIG. 01</span>
                <span className="os-hero__fig os-hero__fig--r">240 GSM</span>
              </div>
            </div>
          </div>
        </section>

        {/* STRIP */}
        <section className="os-strip" aria-label="Key specifications">
          <ul className="os-strip__inner">
            {MARQUEE.map((item, i) => (
              <li key={item} className="os-strip__item">
                {i > 0 && <span className="os-strip__dot" aria-hidden="true" />}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* STATEMENT */}
        <section className="os-statement">
          <div className="os-statement__inner">
            <div className="os-statement__label">
              <OtEyebrow>THE SPEC SHEET</OtEyebrow>
            </div>
            <div className="os-statement__body">
              <p className="os-statement__big">
                Cut wide, kept clean. It moves with you, breathes with you, and looks like you meant it.
              </p>
              <p className="os-statement__small">
                Seven details, each chosen for one thing: how it feels on you.
              </p>
            </div>
          </div>
        </section>

        {/* BENTO */}
        <section className="os-bento">
          <div className="os-bento__inner">
            <article className="os-tile os-tile--ink os-tile--weight">
              <OtEyebrow invert>01 — THE WEIGHT</OtEyebrow>
              <div className="os-weight">
                <span className="os-weight__num">240</span>
                <span className="os-weight__unit">GSM</span>
              </div>
              <p className="os-tile__copy">
                Substantial cotton with enough body to hold its line. Wide, but never shapeless.
              </p>
            </article>

            <article className="os-tile os-tile--mist os-tile--mix">
              <OtEyebrow>02 — THE FABRIC</OtEyebrow>
              <div className="os-mix">
                <span className="os-mix__num">100%</span>
                <div
                  className="os-mix__bar"
                  role="img"
                  aria-label="100 percent cotton"
                >
                  <div className="os-mix__cotton" />
                </div>
                <div className="os-mix__labels">
                  <span>100% COTTON</span>
                </div>
              </div>
              <p className="os-tile__copy">
                Pure cotton, nothing added. It breathes, it softens, and it gets better the more you wear it.
              </p>
            </article>
          </div>
        </section>

        {/* WEIGHT SCALE */}
        <section className="os-scale">
          <div className="os-scale__inner">
            <div className="os-scale__panel">
              <OtEyebrow>03 — WHERE 240 SITS IN THE BLANK</OtEyebrow>
              <p className="os-scale__note">
                A mid-weight with presence: heavier than our everyday tees, still light enough to wear all day.
              </p>

              {/* desktop ruler */}
              <div className="os-scale__scroll">
                <div className="os-scale__track">
                  <div className="os-scale__axis" aria-hidden="true" />
                  {SCALE.map((s) => (
                    <div
                      key={s.gsm}
                      className={`os-scale__tick${s.active ? " os-scale__tick--active" : ""}`}
                      style={{ left: `${scalePos(s.gsm)}%` }}
                    >
                      {s.active ? <span className="os-scale__marker" aria-hidden="true" /> : null}
                      <span className="os-scale__stem" aria-hidden="true" />
                      <span className="os-scale__gsm">{s.gsm}</span>
                      <span className="os-scale__name">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* mobile bars */}
              <ul className="os-bars">
                {SCALE.map((s) => (
                  <li key={s.gsm} className={`os-bar${s.active ? " os-bar--active" : ""}`}>
                    <div className="os-bar__top">
                      <span>{s.label}</span>
                      <span className="os-bar__gsm">{s.gsm}</span>
                    </div>
                    <div className="os-bar__track" aria-hidden="true">
                      <div
                        className="os-bar__fill"
                        style={{ width: `${(s.gsm / SCALE_TOP) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ANATOMY */}
        <section className="os-anatomy">
          <div className="os-anatomy__inner">
            <OtEyebrow>04 — ANATOMY</OtEyebrow>
            <h2 className="os-anatomy__title">The details you can feel.</h2>

            <div className="os-anatomy__grid">
              <div className={`os-diagram${ANATOMY_IMAGE ? " os-diagram--img" : ""}`}>
                {ANATOMY_IMAGE ? (
                  <img className="os-diagram__img" src={ANATOMY_IMAGE} alt={ANATOMY_ALT} loading="lazy" />
                ) : (
                <svg viewBox="0 0 400 400" className="os-diagram__svg" aria-hidden="true">
                  <path
                    d="M130 50 L170 38 Q200 62 230 38 L270 50 L372 118 L336 176 L292 152 L292 352 L108 352 L108 152 L64 176 L28 118 Z"
                    fill="#ffffff"
                    stroke="#0a0a0a"
                    strokeWidth="2"
                    strokeLinejoin="miter"
                  />
                  <path d="M170 38 Q200 62 230 38" fill="none" stroke="#0a0a0a" strokeWidth="7" />
                  <path d="M100 70 L108 152" fill="none" stroke="#0a0a0a" strokeWidth="1.5" strokeDasharray="5 4" />
                  <path d="M300 70 L292 152" fill="none" stroke="#0a0a0a" strokeWidth="1.5" strokeDasharray="5 4" />
                  <path d="M108 336 L292 336" fill="none" stroke="#0a0a0a" strokeWidth="1.5" strokeDasharray="5 4" />
                </svg>
                )}
                {(!ANATOMY_IMAGE || SHOW_MARKERS) && ANATOMY.map((a) => (
                  <span
                    key={a.n}
                    className="os-diagram__marker"
                    style={{ left: `${a.left}%`, top: `${a.top}%` }}
                    aria-hidden="true"
                  >
                    {a.n}
                  </span>
                ))}
              </div>

              <ol className="os-legend">
                {ANATOMY.map((a) => (
                  <li key={a.n} className="os-legend__row">
                    <span className="os-legend__n">{a.n}</span>
                    <div className="os-legend__body">
                      <h3 className="os-legend__title">{a.title}</h3>
                      <p className="os-legend__text">{a.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* UNISEX + IN-HOUSE */}
        <section className="os-make">
          <div className="os-make__inner">
            <article className="os-make__cell">
              <OtEyebrow>05 — UNISEX STYLE</OtEyebrow>
              <h3 className="os-make__title">One cut. Every build.</h3>
              <p className="os-make__text">
                Made for everyone. It fits the way you wear it, not the way a label says you should.
              </p>
            </article>
            <article className="os-make__cell os-make__cell--ink">
              <OtEyebrow invert>06 — MADE IN-HOUSE</OtEyebrow>
              <h3 className="os-make__title">Stitched with intent.</h3>
              <p className="os-make__text">
                Sewn in-house on state-of-the-art machines, from premium 240 GSM, 100% cotton fabric. Made
                carefully, so it sits right and holds up.
              </p>
            </article>
          </div>
        </section>

        {/* CLOSING */}
        <section className="os-close">
          <div className="os-close__inner">
            <div className="os-close__copy">
              <OtEyebrow invert>VESHH-EON</OtEyebrow>
              <p className="os-close__title">
                Put it on.
                <br />
                Feel the difference.
              </p>
              <p className="os-close__tag">Walk Like You Own The Damn Street</p>
            </div>
            <div className="os-close__buy">
              <div className="os-close__row">
                <span>OVERSIZED SHIRTS</span>
              </div>
              <a className="os-btn os-btn--light" href="#fos-collection">
                Shop the collection
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* ═════════ FEATURED PRODUCTS (from original) ═════════ */}
      <section className="fos-root" id="fos-collection">
        {/* HEADER */}
        <div className="fos-header">
          <div>
            <div className="fos-eyebrow">Featured Category</div>
            <h2 className="fos-title">Oversized <span>Shirts</span></h2>
          </div>
          <Link to={COLLECTION_PATH} className="fos-cta-header">
            Shop All
          </Link>
        </div>

        {/* DIVIDER */}
        <div className="fos-rule"><hr /></div>

        {/* GRID */}
        <div className="fos-grid">
          {loading
            ? [...Array(8)].map((_, i) => <div key={i} className="fos-skeleton" />)
            : displayProducts.map((product) => (
                <div key={product._id} className="fos-card">
                  <Link to={`/product/${product._id}`} className="fos-card-link">
                    <div className="fos-img-wrap">
                      <img
                        src={product.images?.[0]?.url}
                        alt={product.name || "Product"}
                        className="fos-img"
                        draggable="false"
                      />
                    </div>
                    <div className="fos-caption">
                      <span className="fos-name">{product.name}</span>
                      <span className="fos-price">₹{product.price}</span>
                    </div>
                  </Link>
                </div>
              ))}
        </div>
      </section>
    </>
  );
};

export default FeaturedOverSizedShirt;
